import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  FileText,
  Search,
  Filter,
  ChevronRight,
  ChevronLeft,
  Calendar,
  Shield,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  ArrowLeft,
  Clock,
  Train,
  Accessibility,
  FileCheck,
  Hash
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Style helpers for recommendation-based styling
const getRecommendationCardStyle = (recommendation) => {
  const styles = {
    reroute: 'bg-amber-500/10 border-amber-500/50',
    proceed: 'bg-emerald-500/10 border-emerald-500/50',
    delay: 'bg-blue-500/10 border-blue-500/50',
    cancel: 'bg-red-500/10 border-red-500/50'
  };
  return styles[recommendation] || 'bg-cyan-500/10 border-cyan-500/50';
};

const getBooleanBadgeStyle = (value) => 
  value ? 'border-emerald-500 text-emerald-400' : 'border-slate-600';

const getBooleanLabel = (value) => value ? 'Required' : 'Not Required';

const getSignedStyle = (isSigned) => 
  isSigned ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-slate-800/50 border-slate-700';

const getSignedIconStyle = (isSigned) => 
  isSigned ? 'text-emerald-400' : 'text-slate-400';

const getSignedBadgeStyle = (isSigned) => 
  isSigned ? 'bg-emerald-500' : 'bg-slate-600';

// Recommendation badge with appropriate color
const RecommendationBadge = ({ recommendation }) => {
  const colors = {
    proceed: 'bg-emerald-500',
    reroute: 'bg-amber-500',
    delay: 'bg-blue-500',
    cancel: 'bg-red-500'
  };
  return (
    <Badge className={colors[recommendation] || 'bg-slate-500'}>
      {recommendation?.toUpperCase()}
    </Badge>
  );
};

// Confidence badge
const ConfidenceBadge = ({ level }) => {
  const colors = {
    high: 'bg-emerald-500',
    medium: 'bg-amber-500',
    low: 'bg-red-500'
  };
  return (
    <Badge variant="outline" className={`${colors[level] || ''} border-current`}>
      {level}
    </Badge>
  );
};

// Risk badge
const RiskBadge = ({ level }) => {
  const colors = {
    LOW: 'text-emerald-400',
    MEDIUM: 'text-amber-400',
    HIGH: 'text-red-400'
  };
  return <span className={`font-medium ${colors[level] || 'text-slate-400'}`}>{level}</span>;
};

// Receipt list item
const ReceiptListItem = ({ receipt, onSelect }) => (
  <button
    onClick={() => onSelect(receipt.run_id)}
    className="w-full text-left p-4 bg-slate-800/50 hover:bg-slate-800 rounded-lg border border-slate-700 hover:border-cyan-500/50 transition-all group"
    data-testid={`receipt-item-${receipt.run_id}`}
  >
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-3">
        <RecommendationBadge recommendation={receipt.recommendation} />
        <ConfidenceBadge level={receipt.confidence_level} />
        {receipt.katlas_signed && (
          <Shield className="w-4 h-4 text-emerald-400" title="Katlas Signed" />
        )}
      </div>
      <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-cyan-400 transition-colors" />
    </div>
    <div className="flex items-center gap-4 text-sm">
      <span className="text-slate-400 font-mono">{receipt.run_id}</span>
      <span className="text-slate-500">|</span>
      <span className="text-slate-400">{receipt.mobility_requirement?.replace(/_/g, ' ')}</span>
      <span className="text-slate-500">|</span>
      <span className="text-slate-400">{receipt.disruption_severity} severity</span>
    </div>
    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
      <span className="flex items-center gap-1">
        <Clock className="w-3 h-3" />
        {new Date(receipt.timestamp).toLocaleString()}
      </span>
      <span>
        {receipt.advisories_accepted} accepted / {receipt.advisories_excluded} excluded
      </span>
    </div>
  </button>
);

// Receipt detail view
const ReceiptDetail = ({ runId, onBack }) => {
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadReceipt = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_URL}/api/passenger/receipts/${runId}`);
        if (res.ok) {
          const data = await res.json();
          setReceipt(data);
        } else {
          setError('Failed to load receipt');
        }
      } catch (err) {
        setError('Failed to load receipt');
      } finally {
        setLoading(false);
      }
    };
    loadReceipt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]); // API_URL is a module constant

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-400 py-8">{error}</div>
    );
  }

  if (!receipt) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-400">Receipt not found</p>
        <Button variant="outline" onClick={onBack} className="mt-4">
          Back to list
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="receipt-detail" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} className="text-slate-400 hover:text-white">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to receipts
        </Button>
        <code className="text-sm text-cyan-400 font-mono">{receipt.run_id}</code>
      </div>

      {/* Decision Summary Card */}
      <Card className={`border-2 ${getRecommendationCardStyle(receipt.decision?.recommendation)}`}>
        <CardHeader className="pb-2">
          <CardTitle className="text-white flex items-center gap-2">
            <Train className="w-5 h-5" />
            Decision Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-4">
            <div>
              <div className="text-xs text-slate-400 mb-1">Recommendation</div>
              <RecommendationBadge recommendation={receipt.decision?.recommendation} />
            </div>
            <div>
              <div className="text-xs text-slate-400 mb-1">Confidence</div>
              <ConfidenceBadge level={receipt.decision?.confidence_level} />
            </div>
            <div>
              <div className="text-xs text-slate-400 mb-1">Risk Change</div>
              <span className="text-sm">
                <RiskBadge level={receipt.risk?.original_journey} /> → <RiskBadge level={receipt.risk?.with_recommendation} />
              </span>
            </div>
          </div>
          <p className="text-slate-300 text-sm">{receipt.decision?.rationale}</p>
        </CardContent>
      </Card>

      {/* Parameters Card */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Accessibility className="w-4 h-4 text-cyan-400" />
            Scenario Parameters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-xs text-slate-500 mb-1">Mobility</div>
              <span className="text-white">{receipt.parameters?.mobility_requirement?.replace(/_/g, ' ')}</span>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Lift Access</div>
              <Badge variant="outline" className={getBooleanBadgeStyle(receipt.parameters?.requires_lift_access)}>
                {getBooleanLabel(receipt.parameters?.requires_lift_access)}
              </Badge>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Staff Assistance</div>
              <Badge variant="outline" className={getBooleanBadgeStyle(receipt.parameters?.requires_staff_assistance)}>
                {getBooleanLabel(receipt.parameters?.requires_staff_assistance)}
              </Badge>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Urgency</div>
              <span className="text-white">{receipt.parameters?.appointment_urgency}</span>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Disruption Severity</div>
              <span className="text-white">{receipt.parameters?.disruption_severity}</span>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Disruption Types</div>
              <div className="flex flex-wrap gap-1">
                {receipt.parameters?.disruption_types?.map(t => (
                  <Badge key={t} variant="outline" className="text-xs">{t.replace(/_/g, ' ')}</Badge>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Evidence Summary Card */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <FileCheck className="w-4 h-4 text-cyan-400" />
            Evidence Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center p-3 bg-slate-900/50 rounded-lg">
              <div className="text-2xl font-bold text-white">{receipt.evidence?.advisories_received}</div>
              <div className="text-xs text-slate-400">Received</div>
            </div>
            <div className="text-center p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
              <div className="text-2xl font-bold text-emerald-400">{receipt.evidence?.advisories_accepted}</div>
              <div className="text-xs text-emerald-400">Accepted</div>
            </div>
            <div className="text-center p-3 bg-red-500/10 rounded-lg border border-red-500/30">
              <div className="text-2xl font-bold text-red-400">{receipt.evidence?.advisories_excluded}</div>
              <div className="text-xs text-red-400">Excluded</div>
            </div>
          </div>
          
          {/* Advisory Sources */}
          <div className="mb-4">
            <div className="text-xs text-slate-500 mb-2">Advisory Sources Configured</div>
            <div className="flex flex-wrap gap-2">
              {receipt.advisory_sources?.verified_operator && (
                <Badge className="bg-emerald-600">Verified Operator</Badge>
              )}
              {receipt.advisory_sources?.verified_network && (
                <Badge className="bg-emerald-600">Verified Network</Badge>
              )}
              {receipt.advisory_sources?.station_staff && (
                <Badge className="bg-emerald-600">Station Staff</Badge>
              )}
              {receipt.advisory_sources?.unverified_included && (
                <Badge className="bg-red-600">Unverified ({receipt.advisory_sources?.unverified_type?.replace(/_/g, ' ')})</Badge>
              )}
            </div>
          </div>

          {/* Excluded Details */}
          {receipt.evidence?.excluded_details?.length > 0 && (
            <div>
              <div className="text-xs text-slate-500 mb-2">Excluded Advisory Details</div>
              <div className="space-y-2">
                {receipt.evidence.excluded_details.map((exc, i) => (
                  <div key={exc.advisory_id || i} className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-white text-sm">{exc.source}</span>
                      <Badge className="bg-red-600 text-xs">EXCLUDED</Badge>
                    </div>
                    <p className="text-slate-400 text-xs">{exc.reason}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Provenance Card */}
      <Card className={`border ${getSignedStyle(receipt.provenance?.katlas_signed)}`}>
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Shield className={`w-4 h-4 ${getSignedIconStyle(receipt.provenance?.katlas_signed)}`} />
            Receipt Metadata
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs text-slate-500 mb-1">Katlas Signed</div>
              <Badge className={getSignedBadgeStyle(receipt.provenance?.katlas_signed)}>
                {receipt.provenance?.katlas_signed ? 'Yes' : 'No'}
              </Badge>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Evidence Chain</div>
              <span className="text-white">{receipt.provenance?.evidence_chain_length} steps</span>
            </div>
            {receipt.provenance?.receipt_hash && (
              <div className="col-span-2">
                <div className="text-xs text-slate-500 mb-1">Receipt Hash</div>
                <code className="text-cyan-400 font-mono text-xs break-all">{receipt.provenance.receipt_hash}</code>
              </div>
            )}
            {receipt.provenance?.signer_fingerprint && (
              <div className="col-span-2">
                <div className="text-xs text-slate-500 mb-1">Signer Fingerprint</div>
                <code className="text-cyan-400 font-mono text-xs break-all">{receipt.provenance.signer_fingerprint}</code>
              </div>
            )}
            <div className="col-span-2">
              <div className="text-xs text-slate-500 mb-1">Timestamp</div>
              <span className="text-white">{new Date(receipt.timestamp).toLocaleString()}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Receipt Viewer component
const ReceiptViewer = () => {
  const [receipts, setReceipts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    recommendation: '',
    katlas_signed: ''
  });
  const [filterOptions, setFilterOptions] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const limit = 10;

  // Load filter options
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const res = await fetch(`${API_URL}/api/passenger/receipts/filters/options`);
        if (res.ok) {
          const data = await res.json();
          setFilterOptions(data);
        }
      } catch (err) {
        // Filter options are optional - silent fail
      }
    };
    loadOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - load once on mount

  // Search receipts
  const searchReceipts = useCallback(async () => {
    setLoading(true);
    setSearchError(null);
    try {
      const params = new URLSearchParams();
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());
      
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.recommendation) params.append('recommendation', filters.recommendation);
      if (filters.katlas_signed !== '') params.append('katlas_signed', filters.katlas_signed);

      const res = await fetch(`${API_URL}/api/passenger/receipts/search?${params}`);
      if (res.ok) {
        const data = await res.json();
        setReceipts(data.results);
        setTotal(data.total);
      } else {
        setSearchError('Search failed');
      }
    } catch (err) {
      setSearchError('Search failed');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, offset, limit]); // API_URL is a module constant

  useEffect(() => {
    searchReceipts();
  }, [searchReceipts]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setOffset(0); // Reset pagination
  };

  const clearFilters = () => {
    setFilters({
      date_from: '',
      date_to: '',
      recommendation: '',
      katlas_signed: ''
    });
    setOffset(0);
  };

  // If viewing a specific receipt
  if (selectedReceipt) {
    return <ReceiptDetail runId={selectedReceipt} onBack={() => setSelectedReceipt(null)} />;
  }

  return (
    <div data-testid="receipt-viewer" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-cyan-400" />
          <h2 className="text-lg font-semibold text-white">Receipt History</h2>
        </div>
        <Badge variant="outline" className="text-slate-400">
          {total} receipts
        </Badge>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <Filter className="w-4 h-4 text-cyan-400" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs text-slate-400">From Date</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                className="bg-slate-900 border-slate-600 text-white h-9"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-400">To Date</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                className="bg-slate-900 border-slate-600 text-white h-9"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Recommendation</Label>
              <select
                value={filters.recommendation}
                onChange={(e) => handleFilterChange('recommendation', e.target.value)}
                className="w-full h-9 rounded-md bg-slate-900 border border-slate-600 text-white text-sm px-3"
              >
                <option value="">All</option>
                {filterOptions?.recommendations?.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Katlas Signed</Label>
              <select
                value={filters.katlas_signed}
                onChange={(e) => handleFilterChange('katlas_signed', e.target.value)}
                className="w-full h-9 rounded-md bg-slate-900 border border-slate-600 text-white text-sm px-3"
              >
                <option value="">All</option>
                <option value="true">Signed</option>
                <option value="false">Not Signed</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button variant="outline" size="sm" onClick={clearFilters} className="h-9 border-slate-600 text-slate-400 hover:text-white">
                Clear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
        </div>
      ) : receipts.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-8 text-center">
            <FileText className="w-10 h-10 text-slate-500 mx-auto mb-3" />
            <p className="text-slate-400">No receipts found</p>
            <p className="text-slate-500 text-sm mt-1">Run some scenarios in the Scenario Lab to generate receipts</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {receipts.map(receipt => (
            <ReceiptListItem
              key={receipt.run_id}
              receipt={receipt}
              onSelect={setSelectedReceipt}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset(Math.max(0, offset - limit))}
            disabled={offset === 0}
            className="border-slate-600 text-slate-400 hover:text-white"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Previous
          </Button>
          <span className="text-sm text-slate-400">
            Showing {offset + 1}-{Math.min(offset + limit, total)} of {total}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset(offset + limit)}
            disabled={offset + limit >= total}
            className="border-slate-600 text-slate-400 hover:text-white"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default ReceiptViewer;
